import React, { Component } from 'react';
import { Button }    from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column }    from 'primereact/column';

import { F } from './RestaurantOrderFields';
import IciciPaymentDialog from '../../Payments/Icici/IciciPaymentDialog';
import razorpayAxios from './razorpay-axios';

const PAYMENT_SHORTCUTS = ['Q','W','E','R','T','Y','U','I','O','P'];

const FALLBACK_PAYMENT = [
    { label: 'Cash',             shortcut: 'Q', value: 1 },
    { label: 'Paytm',            shortcut: 'W', value: 2 },
    { label: 'G Pay',            shortcut: 'E', value: 3 },
    { label: 'Non Chargeable',   shortcut: 'R', value: 4 },
    { label: 'Customer Account', shortcut: 'T', value: 5 },
    { label: 'PhonePay',         shortcut: 'Y', value: 6 },
];

// Dev-only stand-in until the real backend's PaymentType master has its own
// "Razorpay" row — see razorpayCheckout.js. Appended in getPaymentMethods()
// below rather than living inside FALLBACK_PAYMENT, since FALLBACK_PAYMENT
// is only used when this.props.PaymentType is empty — once real master data
// loads (Cash/Paytm/etc from the backend), FALLBACK_PAYMENT (and anything
// only added there) stops being used entirely.
const RAZORPAY_METHOD = { label: 'Razorpay', shortcut: 'U', value: 'razorpay' };

// Same dev-only stand-in arrangement as RAZORPAY_METHOD above, for ICICI
// Eazypay (POS/Payments/Icici). Two entries because ICICI has two distinct
// tenders, not two ways of doing one thing:
//   UPI  -> generateQR, a QR scanned at the counter
//   Card -> initiateSale, ICICI's hosted page (card/netbanking/wallet/UPI)
// Both still await their own rows in the backend's PaymentType master.
const ICICI_UPI_METHOD  = { label: 'ICICI QR',  shortcut: 'I', value: 'icici-upi' };
const ICICI_CARD_METHOD = { label: 'ICICI Card', shortcut: 'O', value: 'icici-card' };

// A payment-method button is treated as an online-gateway trigger by label
// match, since the real value comes from backend master data we don't own the
// shape of. Each gateway is matched separately: they have entirely different
// flows, and getPaymentMethods() has to be able to tell whether each one is
// already present independently of the others.
//
// Anything labelled ICICI that doesn't say UPI/QR routes to the hosted page,
// which is the safe default — the hosted page covers every instrument.
const isRazorpayMethod  = (btn) => /razorpay/i.test(btn?.label || '');
const isIciciMethod     = (btn) => /icici/i.test(btn?.label || '');
const isIciciUpiMethod  = (btn) => isIciciMethod(btn) && /upi|qr/i.test(btn?.label || '');
const isIciciCardMethod = (btn) => isIciciMethod(btn) && !isIciciUpiMethod(btn);

const SummaryRow = ({ label, value, bold }) => (
    <div className="p-d-flex p-jc-between p-px-2 p-py-1">
        <span className="p-text-secondary">{label}</span>
        <span style={bold ? { fontWeight: 700 } : {}}>{value}</span>
    </div>
);

const FieldLabel = ({ children }) => (
    <label
        className="p-d-block p-text-secondary p-mb-1"
        style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}
    >
        {children}
    </label>
);

class BillingScreen extends Component {

    state = {
        tenderAmount:     '',
        selectedMethod:   null,
        gatewayProcessing: false,

        // ICICI: amount/orderId/paymentType are snapshotted when the dialog
        // opens rather than read live, so editing the Enter Amount box behind
        // the dialog can't change what's actually being charged.
        iciciDialogVisible: false,
        iciciTender:        'qr',   // 'qr' (generateQR) | 'hosted' (initiateSale)
        iciciAmount:        0,
        iciciOrderId:       '',
        iciciPaymentType:   null,
    };

    // Popup handle for the hosted-page tender. Deliberately an instance field
    // rather than state: it's a window reference, not render data, and the
    // dialog only reads it once on mount.
    iciciPopup = null;

    // ─────────────────────────────────────────────────────────────────────
    // DATA READERS
    // ─────────────────────────────────────────────────────────────────────

    getBillSummary = () => {
        const b = this.props.stageFields[F.BILL_SUMMARY]?.ContainerFields?.[0];
        if (!b) return { grossAmount: 0, itemDiscount: 0, tax: 0, billDiscount: 0,
                         serviceCharge: 0, packingCharge: 0, deliveryCharge: 0,
                         roundOff: 0, grandTotal: 0 };
        const num = v => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
        return {
            grossAmount:    num(b[F.BILL_GROSS]?.FieldValue),
            itemDiscount:   num(b[F.BILL_ITEM_DISCOUNT]?.FieldValue),
            tax:            num(b[F.BILL_TAX]?.FieldValue),   // cgst , sgst 
            billDiscount:   num(b[F.BILL_DISCOUNT]?.FieldValue),
            serviceCharge:  num(b[F.BILL_SERVICE_CHARGE]?.FieldValue),
            packingCharge:  num(b[F.BILL_PACKING_CHARGE]?.FieldValue),
            deliveryCharge: num(b[F.BILL_DELIVERY_CHARGE]?.FieldValue),
            roundOff:       num(b[F.BILL_ROUND_OFF]?.FieldValue),
            grandTotal:     num(b[F.BILL_GRAND_TOTAL]?.FieldValue),
        };
    };

    // Issue 3 + 4: only Sent rows for display and billing
    getSentLineItems = () => {
        const rows = this.props.stageFields[F.MENU_ITEMS]?.ContainerFields || [];
        return rows
            .filter(row => row[F.MI_KOT_STATUS]?.FieldValue === 'Sent')
            .map((row, idx) => ({
                sno:  idx + 1,
                name: row.__displayName || row[F.MI_ITEM]?.FieldValue,
                qty:  Number(row[F.MI_QTY]?.FieldValue)  || 0,
                rate: Number(row[F.MI_RATE]?.FieldValue) || 0,
                net:  (Number(row[F.MI_QTY]?.FieldValue) || 0) *
                      (Number(row[F.MI_RATE]?.FieldValue) || 0),
            }));
    };

    // Passed to ICICI so the hosted page can prefill and the receipt carries a
    // name. Both are optional there — the service substitutes its own guest
    // email when none is given.
    getCustomer = () => {
        const c = this.props.stageFields[F.CUSTOMER_INFO]?.ContainerFields?.[0];
        return {
            name:   c?.[F.CUST_NAME]?.FieldValue   || '',
            mobile: c?.[F.CUST_MOBILE]?.FieldValue || '',
        };
    };

    getRealPaymentRows = () =>
        (this.props.stageFields[F.PAYMENT_INFO]?.ContainerFields || [])
            .filter(row => Number(row[F.PAY_AMOUNT_TENDERED]?.FieldValue || 0) > 0);

    getTotalPaid = () =>
        this.getRealPaymentRows()
            .reduce((s, row) => s + Number(row[F.PAY_AMOUNT_TENDERED]?.FieldValue || 0), 0);

    // Issue 1: backend row has a real FieldId on any field
    isBackendRow = (row) => {
        const id = row[F.PAY_TYPE]?.FieldId;
        return !!id && id !== '' && id !== '00000000-0000-0000-0000-000000000000';
    };

    getPaymentTypeLabel = value => {
        const match = this.getPaymentMethods().find(p => String(p.value) === String(value));
        return match ? match.label : (value || '—');
    };

    // Single source of truth for the payment-method button list, used by the
    // render buttons, keyboard shortcuts, and payment-history label lookup
    // above. Always makes sure a gateway (Razorpay) entry is present, whether
    // that's this.props.PaymentType (real backend master data) or
    // FALLBACK_PAYMENT (used when master data hasn't loaded yet) — otherwise
    // once real PaymentType rows exist, RAZORPAY_METHOD (only ever added to
    // FALLBACK_PAYMENT) would stop showing up at all.
    getPaymentMethods = () => {
        const base = this.props.PaymentType?.length ? this.props.PaymentType : FALLBACK_PAYMENT;
        const withRazorpay = base.some(isRazorpayMethod) ? base : [...base, RAZORPAY_METHOD];
        const withUpi      = withRazorpay.some(isIciciUpiMethod)
            ? withRazorpay
            : [...withRazorpay, ICICI_UPI_METHOD];
        return withUpi.some(isIciciCardMethod) ? withUpi : [...withUpi, ICICI_CARD_METHOD];
    };

    // ─────────────────────────────────────────────────────────────────────
    // FIELD HELPERSx
    // ─────────────────────────────────────────────────────────────────────

    fmt    = n   => Number(n || 0).toFixed(2);
    setRow = (cKey, fKey, val) => this.props.onUpdateContainerField(cKey, fKey, val);

    // ─────────────────────────────────────────────────────────────────────
    // PAYMENT HANDLERS
    // ─────────────────────────────────────────────────────────────────────

    handleAddPayment = () => {
        const { selectedMethod, tenderAmount } = this.state;
        const bill    = this.getBillSummary();
        const balance = Math.max(0, bill.grandTotal - this.getTotalPaid());

        if (balance <= 0)           return;
        if (!selectedMethod)        return;
        const amount = Number(tenderAmount);
        if (!amount || amount <= 0) return;

        this.props.onAddPayment({
            paymentType:   selectedMethod,
            amount,
            transactionNo: '',
            approvalNo:    '',
            bank:          '',
            cardNo:        '',
            changeReturn:  0,
            status:        'Paid',
        });

        this.setState({ tenderAmount: '' });
    };

    // Gateway methods (Razorpay) use whatever's in the Enter Amount box —
    // same as Cash/Paytm/etc's "Add" button — rather than always the full
    // outstanding balance. tenderAmount already defaults to the remaining
    // balance (see componentDidMount/componentDidUpdate below), so leaving
    // it untouched still pays the full amount; editing it down lets the
    // cashier split e.g. part Cash + part Razorpay. onPayWithRazorpay
    // (RestaurantOrder.js) handles order creation, opening Checkout, and
    // verification, then calls onAddPayment itself on success — same as the
    // manual flow above.
    handleRazorpayPay = (paymentTypeValue) => {
        const { tenderAmount } = this.state;
        const bill    = this.getBillSummary();
        const balance = Math.max(0, bill.grandTotal - this.getTotalPaid());
        const amount  = Number(tenderAmount);

        if (balance <= 0 || !this.props.onPayWithRazorpay) return;
        if (!amount || amount <= 0) return;

        const stopProcessing = () => this.setState({ gatewayProcessing: false });
        this.setState({ gatewayProcessing: true });
        this.props.onPayWithRazorpay(amount, paymentTypeValue).then(stopProcessing, stopProcessing);
    };

    // Both ICICI tenders take the amount the same way handleRazorpayPay does
    // (from the Enter Amount box, so part-Cash/part-ICICI splits work), but
    // the flow is different enough to live in its own dialog: there's no
    // hosted modal to call back, just an out-of-band payment and an outcome
    // we have to poll for. See POS/Payments/Icici/IciciPaymentDialog.js.
    //
    // tender: 'qr' for generateQR, 'hosted' for initiateSale.
    handleIciciPay = (tender, paymentTypeValue) => {
        const bill    = this.getBillSummary();
        const balance = Math.max(0, bill.grandTotal - this.getTotalPaid());
        const amount  = Number(this.state.tenderAmount);

        if (balance <= 0)           return;
        if (!amount || amount <= 0) return;

        // The hosted page needs a popup, and it has to be opened HERE —
        // synchronously inside the click — or the browser won't count it as a
        // user gesture and will block it. By the time the /sale call returns
        // with a URL the gesture is long gone, so the window is opened blank
        // up front and pointed at ICICI once the URL arrives. A full-page
        // redirect isn't an option: it would unmount the POS and lose the
        // in-progress order.
        this.iciciPopup = null;
        if (tender === 'hosted') {
            this.iciciPopup = window.open('', 'icici-payment',
                'width=520,height=740,menubar=no,toolbar=no,location=yes,status=no');

            if (this.iciciPopup) {
                // about:blank is same-origin, so this is allowed — and stops
                // the window looking broken while /sale is in flight.
                this.iciciPopup.document.write(
                    '<title>ICICI Payment</title>' +
                    '<p style="font:14px system-ui;padding:24px">Contacting ICICI…</p>');
            }
        }

        this.setState({
            iciciDialogVisible: true,
            iciciTender:        tender,
            iciciAmount:        amount,
            iciciPaymentType:   paymentTypeValue,
            iciciOrderId:       this.props.stageFields[F.ORDER_NUMBER]?.FieldValue || `pos-${Date.now()}`,
        });
    };

    // Routes a confirmed UPI payment through the same onAddPayment the manual
    // Cash/Paytm "Add" button uses, so it lands in Payment History and the
    // balance recalculates identically — same arrangement as payWithRazorpay
    // in RestaurantOrder.js.
    handleIciciPaid = ({ amount, transactionNo }) => {
        this.props.onAddPayment({
            paymentType:   this.state.iciciPaymentType,
            amount,
            transactionNo: transactionNo || '',
            approvalNo:    '',
            bank:          '',
            cardNo:        '',
            changeReturn:  0,
            status:        'Paid',
        });
    };

    // Issue 1: only allow clearing rows that are NOT from backend
    handleClearPayment = (allRowsIndex, row) => {
        if (this.isBackendRow(row)) return; // guard — should not be called for backend rows

        const clearField = (fKey) =>
            this.props.onUpdateContainerField(F.PAYMENT_INFO, fKey, '', allRowsIndex);

        clearField(F.PAY_TYPE);
        clearField(F.PAY_AMOUNT_TENDERED);
        clearField(F.PAY_CHANGE_RETURN);
        clearField(F.PAY_TRANSACTION_NO);
        clearField(F.PAY_CARD_NO);
        clearField(F.PAY_BANK);
        clearField(F.PAY_APPROVAL_NO);
        clearField(F.PAY_STATUS);
    };

    // ─────────────────────────────────────────────────────────────────────
    // RAZORPAY PAYMENT PICKUP
    // ─────────────────────────────────────────────────────────────────────

    // Payment ids already handed to onAddPayment by the pickup below. The
    // parent's state update is async, so this covers a second lookup landing
    // before the first one's rows show up in props.
    pickedUpPaymentIds = new Set();

    // A Razorpay payment can succeed while the till never hears back: the page
    // reloads before /verify-payment returns, so payWithRazorpay never calls
    // onAddPayment. When the billing screen opens, ask the backend for this
    // bill's captured Razorpay payments (matched on the order number or
    // requestId that payWithRazorpay sends as order notes) and add any that
    // aren't in Payment History yet. They are saved the usual way, when
    // Mark as Paid / Save Partial Payment is clicked.
    loadRazorpayPayments = () => {
        const orderNumber = this.props.stageFields[F.ORDER_NUMBER]?.FieldValue || '';
        const requestId   = this.props.requestId || '';
        if ((!orderNumber && !requestId) || !this.props.onAddPayment) return;

        razorpayAxios.get('/bill-payments', { params: { orderNumber, requestId } })
            .then(({ data }) => {
                if (!this._mounted || !Array.isArray(data)) return;

                const razorpayMethod = this.getPaymentMethods().find(isRazorpayMethod);
                if (!razorpayMethod) return;

                // Read the rows now rather than when the request went out, so a
                // payment added while the lookup was in flight still counts.
                const recorded = new Set(this.pickedUpPaymentIds);
                (this.props.stageFields[F.PAYMENT_INFO]?.ContainerFields || [])
                    .forEach(row => {
                        const txn = row[F.PAY_TRANSACTION_NO]?.FieldValue;
                        if (txn) recorded.add(txn);
                    });

                data
                    .filter(p => p && p.paymentId && p.status === 'captured' && Number(p.amount) > 0)
                    .filter(p => !recorded.has(p.paymentId))
                    .forEach(p => {
                        recorded.add(p.paymentId);
                        this.pickedUpPaymentIds.add(p.paymentId);
                        this.props.onAddPayment({
                            paymentType:   razorpayMethod.value,
                            amount:        Number(p.amount),
                            transactionNo: p.paymentId,
                            approvalNo:    '',
                            bank:          '',
                            cardNo:        '',
                            changeReturn:  0,
                            status:        'Paid',
                        });
                    });
            })
            // A failed lookup mustn't get in the way of billing: the screen
            // carries on exactly as before, just without the pickup.
            .catch(() => {});
    };

    // ─────────────────────────────────────────────────────────────────────
    // LIFECYCLE
    // ─────────────────────────────────────────────────────────────────────

    componentDidMount() {
        this._mounted = true;
        document.addEventListener('keydown', this.handleKeyDown);
        const bill    = this.getBillSummary();
        const balance = Math.max(0, bill.grandTotal - this.getTotalPaid());
        if (balance > 0) this.setState({ tenderAmount: balance.toFixed(2) });
        this.loadRazorpayPayments();
    }

    componentDidUpdate(prevProps) {
        const prevLen = prevProps.stageFields[F.PAYMENT_INFO]?.ContainerFields?.length || 0;
        const currLen = this.props.stageFields[F.PAYMENT_INFO]?.ContainerFields?.length || 0;
        if (currLen !== prevLen) {
            const bill    = this.getBillSummary();
            const balance = Math.max(0, bill.grandTotal - this.getTotalPaid());
            this.setState({ tenderAmount: balance > 0 ? balance.toFixed(2) : '' });
        }
    }

    componentWillUnmount() {
        this._mounted = false;
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    // Where a payment-method button's click goes — shared by the keyboard
    // shortcuts and the rendered buttons so the two can't drift apart.
    triggerMethod = (btn) => {
        if (isIciciUpiMethod(btn))  return this.handleIciciPay('qr', btn.value);
        if (isIciciCardMethod(btn)) return this.handleIciciPay('hosted', btn.value);
        if (isRazorpayMethod(btn))  return this.handleRazorpayPay(btn.value);
        return this.setState({ selectedMethod: btn.value });
    };

    handleKeyDown = (e) => {
        // The QR dialog owns the keyboard while it's open. Without this a
        // shortcut key would fire a second payment method behind the dialog,
        // and Escape would both close the dialog and navigate back to the
        // order — leaving a live QR on the gateway with nothing polling it.
        if (this.state.iciciDialogVisible) return;

        if (['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) return;
        const key  = e.key.toUpperCase();
        const list = this.getPaymentMethods();

        if (!this.props.PaymentType?.length) {
            const match = list.find(m => m.shortcut === key);
            if (match) this.triggerMethod(match);
        } else {
            const idx = PAYMENT_SHORTCUTS.indexOf(key);
            if (idx >= 0 && idx < list.length) this.triggerMethod(list[idx]);
        }
        if (key === 'ESCAPE' && this.props.onBack) this.props.onBack();
    };

    // ─────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────

    render() {
        const { onBack, onCompletePayment, submitting, printBill } = this.props;
        const { tenderAmount, selectedMethod } = this.state;

        const bill      = this.getBillSummary();
        const lineItems = this.getSentLineItems();  // Issue 3
        const realRows  = this.getRealPaymentRows();
        const totalPaid = this.getTotalPaid();
        const balance   = Math.max(0, bill.grandTotal - totalPaid);
        const change    = Math.max(0, totalPaid - bill.grandTotal);
        const isPaidFull = balance <= 0;

        const allPaymentRows = this.props.stageFields[F.PAYMENT_INFO]?.ContainerFields || [];
        const list = this.getPaymentMethods();

        return (
            <div className="p-d-flex p-flex-column" style={{ height: '100%', overflow: 'hidden' }}>

                {/* ══ BODY ══════════════════════════════════════════════════ */}
                <div className="p-grid p-nogutter" style={{ flex: 1, overflow: 'hidden' }}>

                    {/* ── LEFT ─────────────────────────────────────────── */}
                    <div
                        className="p-col-12 p-lg-7 p-d-flex p-flex-column p-p-3"
                        style={{ borderRight: '1px solid #dee2e6', overflowY: 'auto' }}
                    >
                        {/* Issue 3: line items DataTable — Sent rows only */}
                        <h3 className="p-mt-0 p-mb-2">Order Items</h3>
                        <DataTable
                            value={lineItems}
                            className="p-datatable-sm p-datatable-striped p-mb-3"
                            emptyMessage="No items sent to kitchen yet"
                        >
                            <Column field="sno"  header="#"     style={{ width: '3rem' }} />
                            <Column field="name" header="Item" />
                            <Column field="rate" header="Rate"
                                body={r => `₹ ${this.fmt(r.rate)}`}
                                style={{ width: '7rem', textAlign: 'right' }} />
                            <Column field="qty"  header="Qty"
                                style={{ width: '4rem', textAlign: 'center' }} />
                            <Column field="net"  header="Net"
                                body={r => <b>₹ {this.fmt(r.net)}</b>}
                                style={{ width: '7rem', textAlign: 'right' }} />
                        </DataTable>

                        {/* Bill summary */}
                        <h3 className="p-mt-0 p-mb-2">Bill Summary</h3>
                        <div className="p-card p-p-3 p-mb-3">
                            <SummaryRow label="Gross Amount"  value={`₹ ${this.fmt(bill.grossAmount)}`} />
                            <SummaryRow label="Item Discount" value={`- ₹ ${this.fmt(bill.itemDiscount)}`} />
                            <SummaryRow label="Tax"           value={`₹ ${this.fmt(bill.tax)}`} />
                            <SummaryRow label="Bill Discount" value={`- ₹ ${this.fmt(bill.billDiscount)}`} />
                            {bill.serviceCharge  > 0 && <SummaryRow label="Service Charge"  value={`₹ ${this.fmt(bill.serviceCharge)}`} />}
                            {bill.packingCharge  > 0 && <SummaryRow label="Packing Charge"  value={`₹ ${this.fmt(bill.packingCharge)}`} />}
                            {bill.deliveryCharge > 0 && <SummaryRow label="Delivery Charge" value={`₹ ${this.fmt(bill.deliveryCharge)}`} />}
                            <SummaryRow label="Round Off"     value={`₹ ${this.fmt(bill.roundOff)}`} />
                            <div style={{ borderTop: '2px solid #2196F3', marginTop: 8, paddingTop: 8 }}>
                                <SummaryRow label="Grand Total" value={`₹ ${this.fmt(bill.grandTotal)}`} bold />
                            </div>
                        </div>

                        {/* Packing charge + total paid */}
                        <div className="p-card p-p-3 p-mb-3">
                            <div className="p-grid">
                                <div className="p-col-6">
                                    <FieldLabel>Packing Charge</FieldLabel>
                                    <InputText
                                        value={this.props.stageFields[F.BILL_SUMMARY]?.ContainerFields?.[0]?.[F.BILL_PACKING_CHARGE]?.FieldValue || ''}
                                        onChange={e => this.setRow(F.BILL_SUMMARY, F.BILL_PACKING_CHARGE, Number(e.target.value) || 0)}
                                        keyfilter="num"
                                        className="w-full"
                                    />
                                </div>
                                <div className="p-col-6">
                                    <FieldLabel>Total Paid</FieldLabel>
                                    <InputText value={`₹ ${this.fmt(totalPaid)}`} disabled className="w-full" />
                                </div>
                            </div>
                        </div>

                        {/* Payment history */}
                        <div className="p-card p-p-2">
                            <div
                                className="p-d-flex p-jc-between p-px-2 p-py-2"
                                style={{ borderBottom: '1px solid #dee2e6', fontWeight: 600 }}
                            >
                                <span>Payment History</span>
                                <span>₹ {this.fmt(totalPaid)}</span>
                            </div>

                            {realRows.length === 0 && (
                                <div className="p-px-2 p-py-2 p-text-secondary" style={{ fontSize: '0.875rem' }}>
                                    No payments recorded yet
                                </div>
                            )}

                            {realRows.map((row, idx) => {
                                const allIdx      = allPaymentRows.indexOf(row);
                                const fromBackend = this.isBackendRow(row); // Issue 1
                                return (
                                    <div
                                        key={idx}
                                        className="p-d-flex p-jc-between p-ai-center p-px-2 p-py-2"
                                        style={{ borderBottom: '1px solid #f0f0f0' }}
                                    >
                                        <span className="p-d-flex p-ai-center" style={{ gap: '0.5rem' }}>
                                            <i className="pi pi-check-circle" style={{ color: '#4caf50' }} />
                                            <span>{this.getPaymentTypeLabel(row[F.PAY_TYPE]?.FieldValue)}</span>
                                            {/* Issue 1: badge to show it's confirmed on backend */}
                                            {fromBackend && (
                                                <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: 4 }}>
                                                    (saved)
                                                </span>
                                            )}
                                        </span>
                                        <span className="p-d-flex p-ai-center" style={{ gap: '1rem' }}>
                                            <span style={{ fontWeight: 600 }}>
                                                ₹ {this.fmt(row[F.PAY_AMOUNT_TENDERED]?.FieldValue)}
                                            </span>
                                            {/* Issue 1: only show delete for non-backend rows */}
                                            {!fromBackend && !isPaidFull && (
                                                <Button
                                                    icon="pi pi-times"
                                                    className="p-button-text p-button-danger p-button-sm p-button-rounded"
                                                    onClick={() => this.handleClearPayment(allIdx, row)}
                                                    tooltip="Remove payment"
                                                />
                                            )}
                                        </span>
                                    </div>
                                );
                            })}

                            {realRows.length > 0 && (
                                <div
                                    className="p-d-flex p-jc-between p-px-2 p-py-2"
                                    style={{ borderTop: '2px solid #dee2e6', fontWeight: 700 }}
                                >
                                    <span>Total Paid</span>
                                    <span>₹ {this.fmt(totalPaid)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── RIGHT: payment input panel ────────────────────── */}
                    <div
                        className="p-col-12 p-lg-5 p-d-flex p-flex-column p-p-3"
                        style={{ overflowY: 'auto' }}
                    >
                        {/* Balance / change bar */}
                        <div
                            className={`p-d-flex p-jc-between p-ai-center p-px-3 p-py-2 p-mb-3 p-border-round
                                ${isPaidFull ? 'p-message p-message-success' : 'p-message p-message-warn'}`}
                        >
                            <span className="p-text-bold">
                                {isPaidFull ? 'Change to return' : 'Balance due'}
                            </span>
                            <span className="p-text-bold" style={{ fontSize: '1.1rem' }}>
                                ₹ {isPaidFull ? this.fmt(change) : this.fmt(balance)}
                            </span>
                        </div>

                        {/* Payment input — hide once fully paid */}
                        {!isPaidFull && (
                            <div className="p-card p-p-3 p-mb-3">
                                <FieldLabel>Enter Amount</FieldLabel>
                                <div className="p-inputgroup p-mb-3">
                                    <span className="p-inputgroup-addon">₹</span>
                                    <InputText
                                        value={tenderAmount}
                                        placeholder={this.fmt(balance)}
                                        onChange={e => this.setState({ tenderAmount: e.target.value })}
                                        keyfilter="num"
                                        onKeyDown={e => e.key === 'Enter' && this.handleAddPayment()}
                                        className="p-text-right"
                                        autoFocus
                                    />
                                    <Button
                                        label="Add"
                                        icon="pi pi-plus"
                                        className="p-button-success"
                                        onClick={this.handleAddPayment}
                                        disabled={
                                            !selectedMethod ||
                                            !tenderAmount   ||
                                            Number(tenderAmount) <= 0
                                        }
                                    />
                                </div>

                                <div className="p-d-flex p-flex-wrap" style={{ gap: '0.5rem' }}>
                                    {list.map((btn, idx) => {
                                        const shortcut = this.props.PaymentType?.length
                                            ? (PAYMENT_SHORTCUTS[idx] || '')
                                            : (btn.shortcut || '');
                                        const isSelected = String(selectedMethod) === String(btn.value);
                                        const isRazorpay = isRazorpayMethod(btn);
                                        const isIciciUpi = isIciciUpiMethod(btn);
                                        const isIciciCard = isIciciCardMethod(btn);
                                        return (
                                            <Button
                                                key={btn.value}
                                                label={
                                                    // Scoped to Razorpay: gatewayProcessing only tracks
                                                    // its hosted modal, so the ICICI button mustn't claim
                                                    // to be processing while Razorpay is the one busy.
                                                    isRazorpay && this.state.gatewayProcessing
                                                        ? 'Processing...'
                                                        : `${btn.label}${shortcut ? ` (${shortcut})` : ''}`
                                                }
                                                icon={
                                                    isIciciUpi  ? 'pi pi-mobile' :
                                                    isIciciCard ? 'pi pi-globe'  :
                                                    isRazorpay  ? 'pi pi-credit-card' : undefined
                                                }
                                                disabled={this.state.gatewayProcessing}
                                                className={isSelected ? 'p-button-primary' : 'p-button-outlined'}
                                                style={{ minWidth: '140px', flex: '1 0 auto' }}
                                                onClick={() => this.triggerMethod(btn)}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Charge slip */}
                        <div className="p-card p-p-3 p-mb-3">
                            <div className="p-grid">
                                <div className="p-col-6">
                                    <FieldLabel>Receipt No</FieldLabel>
                                    <InputText value="New Receipt" disabled className="w-full" />
                                </div>
                                <div className="p-col-6">
                                    <FieldLabel>Charge Slip No</FieldLabel>
                                    <InputText
                                        value={this.props.stageFields[F.PAYMENT_INFO]?.ContainerFields?.[0]?.[F.PAY_APPROVAL_NO]?.FieldValue || ''}
                                        onChange={e => this.setRow(F.PAYMENT_INFO, F.PAY_APPROVAL_NO, e.target.value)}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Integrated POS placeholder */}
                        <div className="p-card p-p-3">
                            <FieldLabel>Integrated POS</FieldLabel>
                            <Button
                                label="PineLabs"
                                icon="pi pi-credit-card"
                                className="p-button-outlined p-button-secondary w-full"
                                disabled
                            />
                        </div>
                    </div>
                </div>

                {/* ══ FOOTER ════════════════════════════════════════════════ */}
                <div
                    className="p-d-flex p-jc-end p-ai-center p-px-3 p-py-2"
                    style={{ borderTop: '1px solid #dee2e6', gap: '0.5rem', flexShrink: 0 }}
                >
                    <Button
                        label="Edit Order (Esc)"
                        className="p-button-warning"
                        style={{ minWidth: '140px' }}
                        onClick={onBack}
                    />
                    <Button
                        label="Print Bill (P)"
                        className="p-button-outlined p-button-secondary"
                        style={{ minWidth: '140px' }}
                        onClick={printBill}
                    />
                    <Button
                        label="E-Bill"
                        className="p-button-outlined p-button-secondary"
                        style={{ minWidth: '140px' }}
                        disabled
                    />
                    <Button
                        label={
                            submitting  ? 'Processing...' :
                            isPaidFull  ? 'Mark as Paid (F)' :
                                         'Save Partial Payment'
                        }
                        disabled={submitting || realRows.length === 0}
                        className="p-button-success"
                        style={{ minWidth: '160px' }}
                        onClick={() => onCompletePayment(bill, change)}
                    />
                </div>

                {/* Mounted only while open, so every attempt starts from a
                    clean slate — no stale merchantRef, countdown or poll loop
                    carried over from a previous try. */}
                {this.state.iciciDialogVisible && (
                    <IciciPaymentDialog
                        tender={this.state.iciciTender}
                        amount={this.state.iciciAmount}
                        orderId={this.state.iciciOrderId}
                        customerName={this.getCustomer().name}
                        customerMobile={this.getCustomer().mobile}
                        popupWindow={this.iciciPopup}
                        onPaid={this.handleIciciPaid}
                        onHide={() => this.setState({ iciciDialogVisible: false })}
                    />
                )}
            </div>
        );
    }
}

export default BillingScreen;